/// Authentication service for the admin panel
import { jwtVerify, SignJWT } from 'jose';

interface LoginCredentials {
  email: string;
  password: string;
  accessCode: string;
}

interface AuthResponse {
  success: boolean;
  csrfToken?: string;
  message?: string;
  error?: string;
  attemptsLeft?: number;
}

interface TokenPayload {
  userId: string;
  email: string;
  role: string;
  exp: number;
}

class AuthService {
  private accessToken: string | null = null;
  private csrfToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshTimer: NodeJS.Timeout | null = null; // Add refresh timer
  private validationInProgress: Promise<AuthResponse> | null = null; // Prevent concurrent validation calls
  
  constructor() {
    // Initialize with existing tokens if available and start refresh timer
    this.initializeFromStorage();
  }
  
  // Initialize service with existing tokens from localStorage
  private initializeFromStorage(): void {
    if (typeof window === 'undefined') return;
    
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedCsrfToken = localStorage.getItem('csrfToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    
    if (storedAccessToken) {
      this.accessToken = storedAccessToken;
      // Schedule refresh if token is still valid
      if (!this.isTokenExpired(storedAccessToken)) {
        this.scheduleTokenRefresh(storedAccessToken);
      }
    }
    
    if (storedCsrfToken) {
      this.csrfToken = storedCsrfToken;
    }
    
    if (storedRefreshToken) {
      this.refreshToken = storedRefreshToken;
    }
  }
  
  // JWT secret key is only used on the server side now
  // The client only needs to store and send tokens, not verify them cryptographically
  
  // Check if user is authenticated
  public isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }
  
  // Get Access Token
  private getAccessToken(): string | null {
    if (this.accessToken) {
      return this.accessToken;
    }
    
    // Try to get from localStorage if not in memory
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) {
      this.accessToken = storedToken;
      return storedToken;
    }
    
    return null;
  }
  
  // Get CSRF token
  private getCsrfToken(): string | null {
    if (this.csrfToken) {
      return this.csrfToken;
    }
    
    // Try to get from localStorage if not in memory
    const storedToken = localStorage.getItem('csrfToken');
    if (storedToken) {
      this.csrfToken = storedToken;
      return storedToken;
    }
    
    return null;
  }
  
  // Get Refresh Token
  private getRefreshToken(): string | null {
    if (this.refreshToken) {
      return this.refreshToken;
    }
    
    // Try to get from localStorage if not in memory
    const storedToken = localStorage.getItem('refreshToken');
    if (storedToken) {
      this.refreshToken = storedToken;
      return storedToken;
    }
    
    return null;
  }
  
  // Set Access Token
  public setAccessToken(token: string): void {
    this.accessToken = token;
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessToken', token);
    }
    
    // Schedule automatic refresh
    this.scheduleTokenRefresh(token);
  }
  
  // Set CSRF token
  public setCsrfToken(token: string): void {
    this.csrfToken = token;
    localStorage.setItem('csrfToken', token);
  }
  
  // Set Refresh Token
  public setRefreshToken(token: string): void {
    this.refreshToken = token;
    localStorage.setItem('refreshToken', token);
  }
  
  // Clear all tokens and timers
  public clearTokens(): void {
    this.accessToken = null;
    this.csrfToken = null;
    this.refreshToken = null;
    
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    // Clear from localStorage if available
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('csrfToken');
      localStorage.removeItem('refreshToken');
    }
  }
  
  // Decode token without verification
  public decodeToken(token: string): TokenPayload | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Failed to decode token:', e);
      return null;
    }
  }
  
  // Check if token is expired based on its payload 
  // (client-side check only, real validation happens on server)
  public isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload) return true;
    
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  }
  
  // Validate access code
  public async validateAccessCode(accessCode: string): Promise<AuthResponse> {
    try {
      // Use the standard API request format that works for other endpoints
      const response = await fetch('/api/admin/validate-access-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessCode }),
        credentials: 'include', // Important for cookies
      });
      
      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Access code validation error response:', errorText);
        
        // Try to parse as JSON
        try {
          const errorData = JSON.parse(errorText);
          return {
            success: false,
            error: errorData.error || 'Access code validation failed',
          };
        } catch (e) {
          return {
            success: false,
            error: `Access code validation failed: ${response.status} ${response.statusText}`,
          };
        }
      }
      
      // Parse JSON response
      return await response.json();
    } catch (error) {
      console.error('Access code validation error:', error);
      return {
        success: false,
        error: 'Network or server error during access code validation',
      };
    }
  }
  
  // Login
  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Use the AJAX login endpoint
      const response = await fetch('/api/admin/login-ajax', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
          access_code: credentials.accessCode
        }),
        credentials: 'include', // Important for cookies
      });
      
      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Login error response:', errorText);
        
        // Try to parse as JSON
        try {
          const errorData = JSON.parse(errorText);
          return {
            success: false,
            error: errorData.error || 'Login failed',
          };
        } catch (e) {
          return {
            success: false,
            error: `Login failed: ${response.status} ${response.statusText}`,
          };
        }
      }
      
      // Parse JSON response
      const data = await response.json();
      
      if (data.success && data.accessToken) {
        // Store tokens - setAccessToken will automatically schedule refresh
        this.setAccessToken(data.accessToken);
        this.setCsrfToken(data.csrfToken);
        this.setRefreshToken(data.refreshToken);
        
        return {
          success: true,
          csrfToken: data.csrfToken,
          message: 'Login successful',
        };
      }
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: 'Network or server error during login',
      };
    }
  }
  
  // Logout
  public async logout(): Promise<AuthResponse> {
    try {
      const response = await fetch('/api/admin/auth/logout-serverless', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken() || '',
          'Authorization': `Bearer ${this.getAccessToken() || ''}`,
        },
        credentials: 'include', // Important for cookies
      });
      
      const data = await response.json();
      
      // Clear tokens regardless of response
      this.clearTokens();
      
      return data;
    } catch (error) {
      console.error('Logout error:', error);
      
      // Clear tokens even if there's an error
      this.clearTokens();
      
      return {
        success: false,
        error: 'Network or server error during logout',
      };
    }
  }
  
  // Refresh token
  public async refreshAccessToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      const accessToken = this.getAccessToken();
      
      if (!refreshToken || !accessToken) {
        return false;
      }
      
      const response = await fetch('/api/admin/auth/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include',
      });
      
      if (!response.ok) {
        // If refresh fails, clear tokens and force re-login
        this.clearTokens();
        return false;
      }
      
      const data = await response.json();
      
      if (data.success && data.accessToken) {
        this.setAccessToken(data.accessToken); // This will automatically schedule next refresh
        // Update CSRF token if provided
        if (data.csrfToken) {
          this.setCsrfToken(data.csrfToken);
        }
        // Store new refresh token if provided
        if (data.refreshToken) {
          this.setRefreshToken(data.refreshToken);
        }
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearTokens();
      return false;
    }
  }
  
  // Validate and refresh token if needed
  public async validateSession(): Promise<AuthResponse> {
    // If validation is already in progress, return the existing promise
    if (this.validationInProgress) {
      return this.validationInProgress;
    }
    
    // Create and store the validation promise
    this.validationInProgress = this.performValidation();
    
    try {
      const result = await this.validationInProgress;
      return result;
    } finally {
      // Clear the validation lock
      this.validationInProgress = null;
    }
  }
  
  // Internal validation logic (separated to handle promise locking)
  private async performValidation(): Promise<AuthResponse> {
    const accessToken = this.getAccessToken();
      
    // If no access token, user is not authenticated
    if (!accessToken) {
        return {
          success: false,
          error: 'Not authenticated',
        };
      }
      
    // Check if token is expired
    if (this.isTokenExpired(accessToken)) {
      // Try to refresh token
      const refreshed = await this.refreshAccessToken();
      
      if (!refreshed) {
        return {
          success: false,
          error: 'Session expired',
        };
      }
    }
    
    // Validate current token
    try {
      const response = await fetch('/api/admin/auth/validate-ajax', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAccessToken()}`,
          'X-CSRF-Token': this.getCsrfToken() || '',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        // If token is invalid, clear and return failure
        if (errorData.error === 'Invalid token') {
          this.clearTokens();
          return {
            success: false,
            error: 'Invalid session',
          };
        }
        
        return {
          success: false,
          error: errorData.error || 'Session validation failed',
        };
      }
      
      const data = await response.json();
      
      // Update tokens if provided
      if (data.accessToken) {
        this.setAccessToken(data.accessToken);
      }
      
      if (data.csrfToken) {
        this.setCsrfToken(data.csrfToken);
      }
      
      return {
        success: true,
      };
      
    } catch (error) {
      console.error('Session validation error:', error);
      return {
        success: false,
        error: 'Network or server error during session validation',
      };
    }
  }
  
  // Make authenticated API requests (simplified version)
  public async apiRequest<T = any>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
    body?: any
  ): Promise<T> {
    const accessToken = this.getAccessToken();
    const csrfToken = this.getCsrfToken();
    
    if (!accessToken) {
      throw new Error('No access token available');
    }
    
    // Check if token is expired before making request
    if (this.isTokenExpired(accessToken)) {
      // Try to refresh token
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        throw new Error('Session expired and refresh failed');
      }
    }
      
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.getAccessToken()}`,
    };
      
    // Add CSRF token for non-GET requests
    if (method !== 'GET' && csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
      
    const config: RequestInit = {
      method,
      headers,
      credentials: 'include',
    };
      
    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }
      
    const response = await fetch(endpoint, config);
    
    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || `API request failed with status ${response.status}`);
      } catch (e) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }
    }
      
    return await response.json();
  }

  // Schedule automatic token refresh before expiry
  private scheduleTokenRefresh(token: string): void {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    const decoded = this.decodeToken(token);
    if (!decoded) return;
    
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = decoded.exp - now;
    
    // Schedule refresh 5 minutes before expiry (300 seconds)
    // For 1-hour tokens, this means refresh at 55 minutes
    const refreshIn = Math.max((expiresIn - 300) * 1000, 60000); // At least 1 minute
    
    this.refreshTimer = setTimeout(async () => {
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        console.error('Auto-refresh failed, user may need to re-login');
        // Optionally redirect to login or show notification
      }
    }, refreshIn);
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService; 